import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import MobileHomeIcon from '@/assets/icons/probability-bottom-navigation/mobile-home.svg?react';
import MobileHomeActiveIcon from '@/assets/icons/probability-bottom-navigation/mobile-home-active.svg?react';
import MobileLeaderboardIcon from '@/assets/icons/probability-bottom-navigation/mobile-leaderboard.svg?react';
import MobileLeaderboardActiveIcon from '@/assets/icons/probability-bottom-navigation/mobile-leaderboard-active.svg?react';
import MobileFriendsIcon from '@/assets/icons/probability-bottom-navigation/mobile-friends.svg?react';
import MobileFriendsActiveIcon from '@/assets/icons/probability-bottom-navigation/mobile-friends-active.svg?react';
import MobileProfileIcon from '@/assets/icons/probability-bottom-navigation/mobile-profile.svg?react';
import MobileProfileActiveIcon from '@/assets/icons/probability-bottom-navigation/mobile-profile-active.svg?react';
import BotNavIcon from '@/assets/icons/main-bottom-navigation/bot-strokelines.svg?react';
import BotNavActiveIcon from '@/assets/icons/probability-bottom-navigation/mobile-bot-active.svg?react';
import PerformanceIcon from '@/assets/icons/probability-bottom-navigation/perfomance.svg?react';
import PerformanceActiveIcon from '@/assets/icons/probability-bottom-navigation/perfomance-active.svg?react';
import {
	resolveProbabilityShareDescription,
	ProbabilityShareType,
	UserRole,
} from '@sportspredict/shared';

import { ROUTES } from '@/constants';
import { cn, shareLink } from '@/utils';
import { useDispatch } from 'react-redux';
import { openAuthModal } from '@/store/slices/auth-modal.slice';

import {
	getProbabilityEventIdFromPath,
	getProbabilityEventPerformanceSummaryPath,
	getProbabilityEventTabPath,
	PROBABILITY_EVENT_TABS,
	type ProbabilityEventTab,
} from '../../utils/probability-event.utils';
import type { ProbabilityUser } from '../../types/probability-event.types';
import { ResourcesTrophyButton } from '../resources';
import { NewsfeedDigestButton } from '../newsfeed';
import shareSpIcon from '../../../../assets/images/share-sp.svg';
import fullLogo from '../../../../assets/images/logo/full-logo-dark.svg';
import {
	useCreateProbabilityShareLinkMutation,
	useGetProbabilityEventDetailsQuery,
} from '../../store/api/probability-event.api';
import { skipToken } from '@reduxjs/toolkit/query';
import { ShareModal } from '../modals/share.modal';
import {
	PROBABILITY_MIXPANEL_EVENTS,
	trackProbabilityMixpanelEvent,
} from '../../utils/analytics.utils';
import { ArrowLeft } from 'lucide-react';

type WebNavigationTab = ProbabilityEventTab | 'performance-summary';

interface WebNavItem {
	icon: React.ElementType;
	activeIcon?: React.ElementType;
	tab: WebNavigationTab;
	label: string;
}

const WEB_NAV_ITEMS: WebNavItem[] = [
	{
		icon: MobileHomeIcon,
		activeIcon: MobileHomeActiveIcon,
		tab: PROBABILITY_EVENT_TABS.HOME,
		label: 'Home',
	},
	{
		icon: MobileLeaderboardIcon,
		activeIcon: MobileLeaderboardActiveIcon,
		tab: PROBABILITY_EVENT_TABS.LEADERBOARD,
		label: 'Leaderboard',
	},
	{
		icon: MobileFriendsIcon,
		activeIcon: MobileFriendsActiveIcon,
		tab: PROBABILITY_EVENT_TABS.FRIENDS,
		label: 'Following',
	},
	{
		icon: MobileProfileIcon,
		activeIcon: MobileProfileActiveIcon,
		tab: PROBABILITY_EVENT_TABS.PROFILE,
		label: 'Profile',
	},
	{
		icon: BotNavIcon,
		activeIcon: BotNavActiveIcon,
		tab: PROBABILITY_EVENT_TABS.BOTS,
		label: 'Bots',
	},
	{
		icon: PerformanceIcon,
		activeIcon: PerformanceActiveIcon,
		tab: 'performance-summary',
		label: 'Performance',
	},
];

interface ProbabilityEventWebHeaderProps {
	user?: ProbabilityUser;
	eventName?: string;
	eventId?: string;
	isPreStart?: boolean;
}

export function ProbabilityEventWebHeader({
	user,
	eventName,
	eventId,
	isPreStart = false,
}: ProbabilityEventWebHeaderProps) {
	const { pathname } = useLocation();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const activeEventId = eventId || getProbabilityEventIdFromPath(pathname);
	const { data: eventDetails, isLoading: isEventDetailsLoading } =
		useGetProbabilityEventDetailsQuery(activeEventId ? { eventId: activeEventId } : skipToken);
	const partnerLogoUrl =
		eventDetails?.partnerLogoImgVariants?.l ||
		eventDetails?.partnerLogoImgVariants?.m ||
		eventDetails?.partnerLogoImgVariants?.s;
	const displayEventName = eventName?.trim() || 'World Cup Platform';

	const roleNavItems =
		user?.role === UserRole.Admin
			? WEB_NAV_ITEMS.filter(
					({ tab }) =>
						tab !== PROBABILITY_EVENT_TABS.PROFILE &&
						tab !== PROBABILITY_EVENT_TABS.FRIENDS &&
						tab !== PROBABILITY_EVENT_TABS.BOTS &&
						tab !== 'performance-summary'
				)
			: WEB_NAV_ITEMS;
	const navItems = isPreStart
		? roleNavItems.filter(
				({ tab }) => tab === PROBABILITY_EVENT_TABS.HOME || tab === PROBABILITY_EVENT_TABS.PROFILE
			)
		: roleNavItems;

	const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, tab: WebNavigationTab) => {
		if (tab === 'performance-summary') {
			if (!user) {
				e.preventDefault();
				dispatch(openAuthModal());
				return;
			}

			trackProbabilityMixpanelEvent(PROBABILITY_MIXPANEL_EVENTS.PERFORMANCE_SUMMARY_CLICKED, {
				eventId: activeEventId,
				eventName: displayEventName,
				user,
				source: 'desktop_header',
				navigation_from: pathname,
			});
			return;
		}

		if (tab === PROBABILITY_EVENT_TABS.HOME || user) return;

		e.preventDefault();
		dispatch(openAuthModal());
	};

	const handleAuthClick = () => {
		dispatch(openAuthModal());
	};

	const [createShareLink] = useCreateProbabilityShareLinkMutation();
	const [shareModalData, setShareModalData] = useState<{ url: string; message: string } | null>(
		null
	);

	const handleShare = async () => {
		if (!activeEventId) return;
		trackProbabilityMixpanelEvent(PROBABILITY_MIXPANEL_EVENTS.EVENT_SHARE_CLICKED, {
			eventId: activeEventId,
			eventName: displayEventName,
			share_surface: 'web_header',
		});
		const { shortCode } = await createShareLink({
			type: ProbabilityShareType.EVENT,
			data: { eventId: activeEventId },
		}).unwrap();
		const url = `${window.location.origin}/p/${shortCode}`;
		const textParams = { eventName: displayEventName };
		const shareMessage = resolveProbabilityShareDescription(
			ProbabilityShareType.EVENT,
			textParams,
			eventDetails?.shareTexts?.[ProbabilityShareType.EVENT]?.description
		);
		await shareLink({
			url,
			title: shareMessage,
			message: shareMessage,
			onDesktopShare: (u, message) => setShareModalData({ url: u, message }),
		});
	};

	return (
		<header className="fixed left-0 right-0 top-0 z-50 h-[72px] border-y-[0.5px] border-[#DCE3F9] bg-white px-6">
			<div className="mx-auto flex h-full items-center justify-between relative">
				<div className="flex items-center gap-3 w-[312px]">
					<div className="flex h-10 items-center justify-center shrink-0 gap-3">
						<button
							type="button"
							className="flex size-8 shrink-0 items-center justify-center rounded-lg border-[0.5px] border-[#DCE3F9] bg-white px-[7px] pl-[6px] shadow-[0_1px_4px_rgba(0,0,0,0.1)]"
							aria-label="Go back"
							onClick={() => navigate(ROUTES.HOME)}
						>
							<ArrowLeft
								className="h-4 w-4 shrink-0"
								style={{
									width: '16px',
									height: '16px',
									aspectRatio: '1 / 1',
									color: '#5583F6',
								}}
							/>
						</button>
						<img src={fullLogo} alt="Sports Predict" className="h-8 w-[61px]" />
					</div>
					{!isEventDetailsLoading && partnerLogoUrl && (
						<div className="border-l-[0.5px] border-[#E5E5E5] flex items-center gap-1 h-6 pl-3">
							<img
								src={partnerLogoUrl}
								alt="Partner"
								className="max-h-4 max-w-8 object-contain shrink-0"
							/>
							<span className="whitespace-nowrap font-['DM_Sans'] text-[10px] font-medium leading-[13px] tracking-[-0.16px] text-[#848484]">
								Probability Cup by SportsPredict.com
							</span>
						</div>
					)}
				</div>

				<nav className="absolute left-1/2 flex -translate-x-1/2 items-center gap-5 xl:gap-12">
					{navItems.map(({ icon: Icon, activeIcon: ActiveIcon, tab, label }) => {
						const to = activeEventId
							? tab === 'performance-summary'
								? getProbabilityEventPerformanceSummaryPath(activeEventId)
								: getProbabilityEventTabPath(activeEventId, tab)
							: ROUTES.HOME;

						const isActive =
							pathname === to ||
							pathname.endsWith(`/${tab}`) ||
							(tab === PROBABILITY_EVENT_TABS.HOME &&
								(pathname.endsWith(`/${PROBABILITY_EVENT_TABS.MARKETS}`) ||
									pathname.endsWith('/locked-in'))) ||
							(tab === PROBABILITY_EVENT_TABS.PROFILE && pathname.includes('/history/')) ||
							(tab === PROBABILITY_EVENT_TABS.BOTS &&
								(pathname.includes(`/${PROBABILITY_EVENT_TABS.BOTS}`) ||
									pathname.includes('/bot/')));

						return (
							<Link
								key={tab}
								to={to}
								prefetch="viewport"
								onClick={(e) => handleClick(e, tab)}
								className="relative flex items-center gap-2 transition-all hover:scale-[0.98]"
							>
								<div className="relative flex size-4 shrink-0 items-center justify-center">
									{isActive && ActiveIcon ? (
										<ActiveIcon className="size-4 shrink-0" />
									) : (
										<Icon className="size-4 shrink-0" />
									)}
									{isActive && (
										<div className="absolute bottom-[-7px] h-px w-[7px] rounded-[2px] bg-blue-custom-2" />
									)}
								</div>
								<span
									className={cn(
										"font-['DM_Sans'] text-sm font-medium leading-5 tracking-[-0.364px] transition-colors select-none",
										isActive ? 'text-[#0A0A0A]' : 'text-[#474747]'
									)}
								>
									{label}
								</span>
							</Link>
						);
					})}
				</nav>

				<div className="flex items-center gap-3">
					{activeEventId && <NewsfeedDigestButton eventId={activeEventId} />}
					<ResourcesTrophyButton layout="web" />
					{user ? (
						<>
							<button
								type="button"
								onClick={handleShare}
								className="bg-white border-[0.5px] border-[#DCE3F9] shadow-[0_1px_2px_rgba(12,12,13,0.05)] hover:bg-[#F8FAFC] flex items-center justify-center rounded-lg shrink-0 size-8 transition-all hover:scale-[0.98]"
								aria-label="Share event"
							>
								<img src={shareSpIcon} alt="" className="size-4" />
							</button>
						</>
					) : (
						<button
							type="button"
							onClick={handleAuthClick}
							className="flex h-8 shrink-0 items-center justify-center rounded-lg bg-[linear-gradient(108deg,#1F4A94_0%,#1C2D4A_49.66%,#141821_80.32%)] px-[18px] pb-px text-sm font-medium tracking-[-0.36px] text-white shadow-[0_1px_4px_rgba(12,12,13,0.05)] transition-all hover:scale-[0.98] active:scale-[0.97] focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-custom-2"
						>
							Sign In
						</button>
					)}
				</div>
			</div>

			{shareModalData && (
				<ShareModal
					open={!!shareModalData}
					url={shareModalData.url}
					message={shareModalData.message}
					onClose={() => setShareModalData(null)}
				/>
			)}
		</header>
	);
}
